<template>
  <resource-list-wrapper
    :class="[listPage && !totalItems && !noData && !loading ? 'wrapper-move-top' : '']"
    :hidden="listPage && !totalItems && !noData && !loading"
    :title="listTitle"
    :sub-title="listSubTitle">
    <template #actions>
      <v-btn
        v-if="totalItems || noData || loading"
        color="primary"
        :outlined="alternativeView"
        class="left-icon"
        :to="listPage ? { name: 'pages_create' } : { name: 'pages_create', query: { domainId } }">
        <plus-icon :class="alternativeView ? 'fill--primary' : 'fill--same-white'" class="mr-2"></plus-icon>
        {{ $t("resources.pages.create") }}
      </v-btn>
    </template>

    <template #default>
      <!--EDIT ROLE MODAL -->
      <Modal
        width="1500"
        v-model="copyToAnotherDomainModal"
        :title="$tc('resources.pages.copy_modal_title', bulkSelectedPages.length)"
        :subtitle="$t('resources.pages.copy_modal_subtitle')"
        :cta-text="$t('resources.pages.copy_modal_cta_title')"
        :cta-loading="copyToAnotherDomainLoading"
        :cta-disabled="!copyToAnotherDomainData.domains"
        :dialog-icon="'general'"
        actions-border
        @confirm="copyToAnotherDomain"
        @cancel="resetCopyToAnotherDomainData">
        <div class="edit-role__content-wrapper">
          <va-autocomplete-input
            :key="copyToAnotherDomainData.inputKey"
            class="mt-4"
            v-model="copyToAnotherDomainData.domains"
            :label="$t('resources.pages.copy_modal_domain_label')"
            :placeholder="$t('resources.pages.copy_modal_domain_placeholder')"
            :choices="domainsList"
            :hide-details="'auto'"
            cache-items
            required
            multiple
            hide-select-all>
          </va-autocomplete-input>
          <!-- Error Display -->
          <div v-if="errorData" class="mt-4">
            <div class="text-body-1"><strong>Error occurred:</strong></div>
            <div v-for="(errors, domainId) in errorData" :key="domainId" class="mb-3">
              <strong class="error--text">{{ getDomainName(domainId) }}:</strong>
              <ul class="mt-1">
                <li v-for="error in errors" :key="error">{{ error }}</li>
              </ul>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        :value="archivePopupData.value"
        :title="archivePopupData.title"
        :subtitle="archivePopupData.subtitle"
        :cta-text="archivePopupData.ctaText"
        @input="closeModal"
        @confirm="confirmModalAction" />

      <va-list
        ref="list"
        :hide-domain-filter="alternativeView"
        :resource="resourceName"
        :disable-query-string="alternativeView"
        :filter.sync="customFiltersValues"
        :filters="filters"
        :domains-list="domainsList"
        :labels-list="labelsList"
        disable-bulk-delete
        disable-export
        disable-create
        dynamic-columns-enabled
        :ignore-domain-filter="!!domainId"
        :update-list-state-data-fn="updateListStateDataFn"
        :append-filter-query-fn="appendFilterQueryFn"
        :columns="fields"
        @update:selected="getBulkSelectedPages"
        @no-data="handleNoData"
        @update:total="handleTotalItems"
        @update:loading="handleLoading"
        @update:columns="updateColumns"
        :create-button-label="$t('generic.buttons.create')">
        <template #no-data="{ listState }">
          <empty-pages-list
            @filterArchivedPages="updateSearchFilters('status', [3])"
            :domainId="domainId"
            :list-page="listPage"
            :list-state="listState"></empty-pages-list>
        </template>
        <template #no-results>
          <div class="no-results">
            {{ $t("custom.no_data") }}
          </div>
        </template>
        <template v-slot:[`bulk.actions`]>
          <div class="d-flex align-center">
            <v-btn @click="copyToAnotherDomainModal = true" text v-if="bulkSelectedPages.length > 1">
              <PagesIcon class="mr-2" :size="16"></PagesIcon>
              <span>
                {{ $tc("resources.pages.copy_to_another_domain", bulkSelectedPages.length) }}
              </span>
            </v-btn>

            <div v-if="!unArchiveBulkAction">
              <v-btn
                @click="editResource('pages', bulkSelectedPages[0].id)"
                v-if="bulkSelectedPages.length < 2"
                text
                color="primary">
                <v-icon small>mdi-pencil-outline</v-icon>
                <span class="ml-2">{{ $t("generic.buttons.edit") }}</span>
              </v-btn>
              <v-btn
                @click="pageDuplicate(bulkSelectedPages[0].id)"
                v-if="bulkSelectedPages.length < 2"
                text
                color="primary">
                <v-icon small>mdi-file-multiple-outline</v-icon>
                <span class="ml-2">{{ $t("generic.buttons.duplicate") }}</span>
              </v-btn>
              <va-bulk-action-button
                :label="$t('resources.pages.archive')"
                icon="mdi-package-down"
                color="primary"
                :action="{ status: 3 }"
                custom-end-point="pages/status"
                @input="actionAfterBulkDelete(bulkSelectedPages.length)"
                @confirmModal="openModalForBulkAction"
                :confirmDeleteAction="confirmBulkDeleteAction"
                text>
              </va-bulk-action-button>
            </div>
            <div v-else>
              <va-bulk-action-button
                label="Unarchive"
                icon="mdi-package-down"
                color="primary"
                :action="{ status: 1 }"
                custom-end-point="pages/status"
                @input="actionAfterBulkDelete(bulkSelectedPages.length)"
                @confirmModal="openModalForBulkAction"
                :confirmDeleteAction="confirmBulkDeleteAction"
                text>
              </va-bulk-action-button>
            </div>
          </div>
        </template>
        <template #[`filter.q`]="props">
          <v-text-field
            class="search-filter"
            hide-details
            v-bind="props"
            label=""
            :placeholder="$t('generic.search_label')"
            @input="val => updateSearchFilters('q', val)">
          </v-text-field>
        </template>
        <template #[`filter.qInclude`]>
          <v-menu
            v-if="ability.can('view', 'advanced content search')"
            offset-y
            :close-on-content-click="false"
            :left="true"
            v-model="isOpenContentSearchDropdown">
            <template v-slot:activator="{ on, value }">
              <v-btn class="content-search-button" text color="primary" v-on="on">
                <span class="mr-2">{{ $t("generic.advanced_settings") }}</span>
                <v-icon v-if="value" small>mdi-chevron-up</v-icon>
                <v-icon v-else small>mdi-chevron-down</v-icon>
              </v-btn>
            </template>
            <div class="content-search-dropdown">
              <v-radio-group v-model="advancedContentSearchValue">
                <v-radio
                  v-for="(item, index) in ADVANCED_CONTENT_SEARCH"
                  :key="index"
                  color="primary"
                  :value="item.value"
                  :label="item.text">
                </v-radio>
              </v-radio-group>

              <div class="content-search-dropdown-buttons">
                <v-btn class="px-8 flex-1" @click="isOpenContentSearchDropdown = false" plain>
                  {{ $t("generic.buttons.cancel") }}
                </v-btn>
                <v-btn
                  class="px-8 flex-1"
                  color="primary"
                  @click="
                    updateSearchFilters('qInclude', advancedContentSearchValue);
                    isOpenContentSearchDropdown = false;
                  ">
                  {{ $t("generic.buttons.apply") }}
                </v-btn>
              </div>
            </div>
          </v-menu>
        </template>

        <va-data-table
          disable-delete
          :fields="activeColumns"
          class="data-table-custom-class va-table--selectable page-table"
          @row-click="handleRowClick"
          dynamic-width
          actionsColWidth="64px">
          <template #[`cell.actions`]="{ item }">
            <v-tooltip v-if="item.status === 2" top>
              <template #activator="{ on }">
                <v-btn icon color="accent" @click.stop="visitLivePage(item.domain, item.slug)" v-on="on">
                  <span class="v-btn__content">
                    <v-icon style="width: 18px">
                      {{ icons.mdiOpenInNew }}
                    </v-icon>
                  </span>
                </v-btn>
              </template>
              <span>{{ $t("resources.pages.visit_page") }} </span>
            </v-tooltip>
            <v-tooltip v-if="item.status === 1 || item.status === 3" top>
              <template #activator="{ on }">
                <v-btn icon color="info" @click.stop="preview(item.slug, item.domain.id)" v-on="on">
                  <span class="v-btn__content">
                    <v-icon style="width: 18px">
                      {{ icons.mdiEye }}
                    </v-icon>
                  </span>
                </v-btn>
              </template>
              <span>{{ $t("resources.pages.preview") }} </span>
            </v-tooltip>

            <DataListActions
              v-if="item.status === 1"
              :actions="[
                { title: 'Edit', icon: 'mdi-pencil-outline', function: editResource.bind(this, 'pages', item.id) },
                {
                  title: 'Preview',
                  icon: 'mdi-eye',
                  function: preview.bind(this, item.slug, item.domain.id, null),
                },
                {
                  title: 'Duplicate',
                  icon: 'mdi-file-multiple-outline',
                  function: pageDuplicate.bind(this, item.id),
                },
                { title: 'Archive', icon: 'mdi-package-down', function: openModal.bind(this, item) },
              ]">
            </DataListActions>
            <DataListActions
              v-if="item.status === 2"
              :actions="[
                { title: 'Edit', icon: 'mdi-pencil-outline', function: editResource.bind(this, 'pages', item.id) },
                {
                  title: 'Preview',
                  icon: 'mdi-eye',
                  function: preview.bind(this, item.slug, item.domain.id, null),
                },
                {
                  title: 'Visit page',
                  icon: 'mdi-open-in-new',
                  function: visitLivePage.bind(this, item.domain, item.slug),
                },
                { title: 'Duplicate', icon: 'mdi-file-multiple-outline', function: pageDuplicate.bind(this, item.id) },
                { title: 'Archive', icon: 'mdi-package-down', function: openModal.bind(this, item) },
              ]">
            </DataListActions>
            <DataListActions
              v-if="item.status === 3"
              :actions="[
                {
                  title: 'Preview',
                  icon: 'mdi-eye',
                  function: preview.bind(this, item.slug, item.domain.id, null),
                },
                { title: 'Unarchive', icon: 'mdi-package-down', function: unArchive.bind(this, item) },
              ]">
            </DataListActions>
          </template>
          <template #[`field.status`]="{ value }">
            <v-chip :color="findStatusName(value).chipColor">{{ findStatusName(value).text }}</v-chip>
          </template>
          <template #[`field.headline`]="{ item }">
            <div class="no-style text--primary cell-ellipsis">
              <span>{{ item.headline }}</span>
            </div>
          </template>
          <template #[`field.slug`]="{ item }">
            <div class="cell-ellipsis">
              <span>{{ item.slug }}</span>
            </div>
          </template>
          <template #[`field.domain.name`]="{ item }">
            <div class="cell-ellipsis">
              <span>{{ item.domain.name }}</span>
            </div>
          </template>
          <template #[`field.type`]="{ value }">
            {{ findTypeName(value) }}
          </template>
          <template #[`field.label.name`]="{ item }">
            <v-tooltip top v-if="item.additionalLabels.length > 0 && item.label">
              <template #activator="{ on }">
                <div v-on="on" class="labels-wrapper">
                  <v-chip
                    :color="getChipColor(item.label, $vuetify.theme.dark)"
                    :style="{ color: getChipTextColor(item.label, $vuetify.theme.dark) }">
                    {{ item.label.name }}
                  </v-chip>
                  {{ `+${item.additionalLabels.length}` }}
                </div>
              </template>
              {{ getAdditionalLabels(item) }}
            </v-tooltip>
            <v-chip
              v-else-if="item.label"
              :color="getChipColor(item.label, $vuetify.theme.dark)"
              :style="{ color: getChipTextColor(item.label, $vuetify.theme.dark) }">
              {{ item.label.name }}
            </v-chip>
            <v-tooltip top v-else-if="item.additionalLabels.length > 1 && !item.label">
              <template #activator="{ on }">
                <div v-on="on" class="labels-wrapper">
                  <v-chip
                    :color="getChipColor(item.additionalLabels[0], $vuetify.theme.dark)"
                    :style="{ color: getChipTextColor(item.additionalLabels[0], $vuetify.theme.dark) }">
                    {{ item.additionalLabels[0].name }}
                  </v-chip>
                  {{ `+${item.additionalLabels.length - 1}` }}
                </div>
              </template>
              {{ getAdditionalLabels(item, true) }}
            </v-tooltip>
            <v-chip
              v-else-if="item.additionalLabels.length === 1"
              :color="getChipColor(item.additionalLabels[0], $vuetify.theme.dark)"
              :style="{ color: getChipTextColor(item.additionalLabels[0], $vuetify.theme.dark) }">
              {{ item.additionalLabels[0].name }}
            </v-chip>
          </template>
          <template #`field.sessions.yesterdayCount`="{ item }">
            {{ item }}
          </template>
          <template #`field.sessions.last7DaysCount`="{ item }">
            {{ item }}
          </template>
          <template #[`field.updatedAt`]="{ item }">
            {{ changeDateFormat(item.updatedAt, isUserUS($store.state.user.timezone)) }}
          </template>
        </va-data-table>
      </va-list>
    </template>
  </resource-list-wrapper>
</template>

<script>
  import definedAbility from "@/ability";
  import { ADVANCED_CONTENT_SEARCH, PAGE_STATUSES, PAGE_TYPES } from "../../constant/index";
  import { mdiImageArea, mdiOpenInNew, mdiTrashCan, mdiEye } from "@mdi/js";
  import Preview from "@/mixins/previewMethod";
  import { changeDateFormat } from "@/@core/utils/filter";
  import { isUserUS } from "@/utility/isUserUS";
  import ResourceListWrapper from "@/components/resourceWrappers/ResourceListWrapper";
  import PlusIcon from "@/components/icons/PlusIcon";
  import DataListActions from "@/components/custom/DataListActions";
  import Modal from "@/components/custom/Modal";
  import store from "@/store";
  import ConnectedDomainsList from "@/mixins/connectedDomainsList";
  import RowClick from "@/mixins/rowClickMixin";
  import debounce from "lodash.debounce";
  import EmptyPagesList from "@/components/custom/EmptyPagesList.vue";
  import get from "lodash/get";
  import PagesIcon from "@/components/icons/PagesIcon";
  import { formErrorMessage } from "@/utility/formErrorMessage";
  import { getChipColor, getChipTextColor } from "@/assets/utilities/helpers/getChipColor";

  export default {
    components: {
      PlusIcon,
      ResourceListWrapper,
      DataListActions,
      Modal,
      EmptyPagesList,
      PagesIcon,
    },
    mixins: [Preview, ConnectedDomainsList, RowClick],
    props: {
      listTitle: {
        type: String,
        default() {
          return this.$t("resources.pages.all_pages");
        },
      },
      listSubTitle: {
        type: String,
        default: "",
      },
      alternativeView: {
        type: Boolean,
        default: false,
      },
      resourceName: {
        type: String,
        default() {
          return this.$route.meta.resource;
        },
      },
      activeFilterOnload: {
        type: Object,
        default: () => ({}),
      },
      /**
       * True if component is used in VA-admin generated route for resource list;
       * False when used elsewhere (Single domain view for example).
       */
      listPage: {
        type: Boolean,
        default: true,
      },
      domainId: {
        type: String,
        default: "",
      },
    },
    data() {
      return {
        ability: definedAbility(),
        changeDateFormat,
        isUserUS,
        getChipColor,
        getChipTextColor,
        noData: false,
        loading: true,
        totalItems: 0,
        labelsList: [],
        icons: {
          mdiImageArea,
          mdiOpenInNew,
          mdiTrashCan,
          mdiEye,
        },
        store: store,
        customFiltersValues: this.activeFilterOnload,
        filters: [
          {
            source: "q",
            alwaysOn: true,
          },
          {
            source: "qInclude",
            alwaysOn: true,
          },
          {
            source: "status",
          },
          {
            source: "label",
          },
        ],
        fields: [
          {
            source: "headline",
            label: this.$t("resources.pages.fields.headline"),
            sortable: true,
            full_width: "40%",
            id: "headline",
          },
          {
            source: "slug",
            label: this.$t("resources.pages.fields.slug"),
            sortable: true,
            full_width: "35%",
            required: true,
            active: true,
            id: "slug",
          },
          {
            source: "status",
            label: this.$t("resources.pages.fields.status"),
            sortable: true,
            full_width: "100px",
            id: "status",
          },
          {
            source: "label.name",
            label: this.$t("resources.pages.fields.label"),
            sortable: true,
            full_width: "132px",
            cellClass: "label-cell",
            id: "label_name",
          },
          {
            source: "updatedAt",
            label: this.$t("resources.pages.fields.updated_at"),
            sortable: true,
            full_width: "110px",
            id: "updated_at",
          },
        ],
        archivePopupData: {
          value: false,
          title: "",
          subtitle: "",
          ctaText: "",
        },
        archivePageId: null,
        confirmBulkDeleteAction: false,
        unArchiveBulkAction: false,
        bulkSelectedPages: [],
        isOpenContentSearchDropdown: false,
        ADVANCED_CONTENT_SEARCH,
        advancedContentSearchValue: "",
        activeColumns: [],
        // Copy to another domain modal data
        copyToAnotherDomainModal: false,
        copyToAnotherDomainLoading: false,
        copyToAnotherDomainData: {
          domains: null,
          inputKey: 0,
        },
        // Simple error display
        errorData: null,
      };
    },
    watch: {
      "$route.query": {
        handler: function (newVal) {
          this.unArchiveBulkAction = false;

          const { filter } = newVal;
          if (filter) {
            const parsedFilterValue = JSON.parse(filter);

            if (parsedFilterValue?.status?.length === 1 && parsedFilterValue?.status[0] === 3) {
              this.unArchiveBulkAction = true;
            }

            if (parsedFilterValue?.qInclude) {
              this.advancedContentSearchValue = parsedFilterValue.qInclude;
            } else {
              this.advancedContentSearchValue = "";
            }
          } else {
            this.advancedContentSearchValue = "";
          }
        },
        deep: true,
        immediate: true,
      },
      "copyToAnotherDomainData.domains": {
        handler: function (newDomains) {
          if (this.errorData && newDomains) {
            // Remove errors for domains that are no longer selected
            const updatedErrors = {};
            Object.keys(this.errorData).forEach(domainId => {
              if (newDomains.includes(domainId)) {
                updatedErrors[domainId] = this.errorData[domainId];
              }
            });
            this.errorData = Object.keys(updatedErrors).length > 0 ? updatedErrors : null;
          }
        },
        deep: true,
      },
    },
    created() {
      if (!this.alternativeView) {
        this.filters.unshift({
          source: "domain",
          type: "select",
          attributes: {
            reference: "domains",
            itemText: "name",
          },
        });
        this.fields.splice(1, 0, {
          source: "domain.name",
          label: this.$t("resources.pages.fields.domain"),
          sortable: true,
          full_width: "25%",
          id: "domain_name",
        });
      }
      if (this.ability.can("view", "type column")) {
        this.fields.splice(2, 0, {
          source: "type",
          label: this.$t("resources.pages.fields.type"),
          sortable: true,
          full_width: "64px",
          id: "type",
        });
      }
      if (this.ability.can("view", "type filter")) {
        this.filters.splice(2, 0, {
          source: "type",
        });
      }
      if (this.ability.can("view", "session count")) {
        this.fields.splice(4, 0, {
          source: "session.last24HoursCount",
          label: this.$t("resources.pages.fields.visits_24h"),
          sortable: true,
          full_width: "141px",
          id: "session_yesterday_count",
        });
        this.fields.splice(5, 0, {
          source: "session.last7DaysCount",
          label: this.$t("resources.pages.fields.sessions_total"),
          sortable: true,
          full_width: "132px",
          id: "session_last_7_days_count",
        });
      }
    },
    async mounted() {
      try {
        const { data } = await this.$admin.dataProvider.getList("labels", { disablePagination: true });
        this.labelsList = data;
      } catch (e) {
        this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
      }

      if (this.$store.state?.table?.page?.activeColumns?.length > 0) {
        this.fields.forEach(field => {
          if (this.$store.state.table.page.activeColumns.includes(field.id) || field.required) {
            this.$set(field, "active", true);
          }
        });
        this.updateColumns(this.fields, false);
      } else {
        this.$store.state.table.page.activeColumns = this.fields.map(field => field.id);
        this.fields = this.fields.map(field => {
          field.active = true;

          return field;
        });
        this.updateColumns(this.fields);
      }
    },
    methods: {
      resetCopyToAnotherDomainData() {
        this.copyToAnotherDomainData.domains = null;
        this.copyToAnotherDomainData.inputKey++;
        this.copyToAnotherDomainModal = false;
        this.errorData = null;
      },
      getDomainsCanBeCopied(errors) {
        const domains = this.copyToAnotherDomainData.domains;

        if (!errors || !domains || !Array.isArray(domains)) {
          return {};
        }

        // Create mapping of domain key to error value
        const domainErrorMap = {};
        const errorIndices = new Set();

        Object.keys(errors).forEach(key => {
          // Extract index from keys like "domains[0]", "domains[1]", etc.
          const match = key.match(/^domains\[(\d+)\]$/);

          if (match) {
            const index = parseInt(match[1], 10);
            errorIndices.add(index);

            // Map domain key to error value
            if (domains[index]) {
              domainErrorMap[domains[index]] = errors[key];
            }
          }
        });

        return domainErrorMap;
      },
      getDomainName(domainId) {
        const domain = this.domainsList.find(d => d.value === domainId);

        return domain ? domain.text : domainId;
      },

      async copyToAnotherDomain() {
        this.copyToAnotherDomainLoading = true;

        try {
          await this.$admin.dataProvider.customHttpCall("POST", "pages/copy", {
            pages: this.bulkSelectedPages.map(page => page["@id"]),
            domains: this.copyToAnotherDomainData.domains,
          });
          this.$admin.toast.success(this.$t("resources.pages.pages_copied_successfully"));
          this.resetCopyToAnotherDomainData();
          await this.$store.dispatch("api/refresh", "pages");
        } catch (e) {
          // create custom error to display in the modal
          this.errorData = this.getDomainsCanBeCopied(e.errors);
        } finally {
          this.copyToAnotherDomainLoading = false;
        }
      },
      updateColumns(columns, updateState = true) {
        this.activeColumns = columns.filter(field => !!field.active);
        if (updateState) {
          this.$store.commit(
            "SET_PAGE_TABLE_COLUMNS",
            this.activeColumns.map(field => field.id),
          );
        }
        if (this.activeColumns.length !== this.fields.length) {
          this.activeColumns.forEach(field => {
            this.$set(field, "width", undefined);
          });
        } else {
          this.activeColumns.forEach(field => {
            this.$set(field, "width", field.full_width);
          });
        }
      },
      getAdditionalLabels(item, removeFirst = false) {
        if (removeFirst) {
          return item.additionalLabels
            .slice(1)
            .map(label => label.name)
            .join(", ");
        }

        return item.additionalLabels.map(label => label.name).join(", ");
      },
      handleNoData(val) {
        this.noData = val;
      },
      handleLoading(val) {
        this.loading = val;
      },
      handleTotalItems(val) {
        this.totalItems = val;
      },
      appendFilterQueryFn(filters) {
        // When no filters are set, filter for 'draft' and 'published' pages.
        const defaultStatusFilter = [1, 2];
        // Current filters values that are set via UI.
        const currentStatusFilter = get(filters, "status");

        return {
          ...filters,
          status: currentStatusFilter ? currentStatusFilter : defaultStatusFilter,
        };
      },
      async updateListStateDataFn(items) {
        let excludedItemsCount = 0;

        if (this.ability.can("view", "session count")) {
          items.map(item => {
            if (!item.session || item.session.length === 0) {
              item.session = {
                yesterdayCount: "-",
                last7DaysCount: "-",
              };
            }
          });
        }

        if (items.length === 0) {
          const { data: archivedPages } = await this.$admin.dataProvider.getList("pages", {
            disablePagination: true,
            filter: {
              ...(() => {
                return this.domainId ? { domain: this.domainId } : {};
              })(),
              status: [3],
            },
          });
          excludedItemsCount = archivedPages.length;
        }

        return {
          excludedItemsCount,
        };
      },
      getBulkSelectedPages(val) {
        this.bulkSelectedPages = val;
      },
      pageDuplicate(itemId) {
        this.$router.push({ name: "pages_create", query: { source: itemId } });
      },
      actionAfterBulkDelete(count) {
        this.archivePopupData.value = false;
        if (!this.unArchiveBulkAction) {
          this.$admin.toast.success(this.$tc("resources.pages.archived_successfully", count));
        } else {
          this.$admin.toast.success(this.$tc("resources.pages.unarchived_successfully", count));
        }
        this.confirmBulkDeleteAction = false;
      },
      openModalForBulkAction(pagesCount) {
        if (!this.unArchiveBulkAction) {
          this.archivePopupData = {
            value: true,
            title: this.$t("resources.pages.archive_any_pages_title", { pages_count: pagesCount }),
            subtitle: this.$t("resources.pages.archive_any_pages_text"),
            ctaText: "Archive",
          };
        } else {
          this.archivePopupData = {
            value: true,
            title: this.$t("resources.pages.unarchive_any_pages_title", { pages_count: pagesCount }),
            subtitle: "",
            ctaText: "Unarchive",
          };
        }
      },
      async confirmModalAction() {
        if (this.archivePageId) {
          await this.$admin.dataProvider.customHttpCall("patch", `pages/${this.archivePageId}`, {
            status: 3,
          });

          this.archivePopupData.value = false;
          await this.$store.dispatch("api/refresh", "pages");
          this.$admin.toast.success(this.$tc("resources.pages.archived_successfully", 1));
          this.archivePageId = null;
        } else {
          this.confirmBulkDeleteAction = true;
        }
      },
      closeModal(value) {
        this.archivePopupData.value = value;
        this.archivePageId = null;
      },
      openModal(item) {
        this.archivePopupData = {
          value: true,
          title: this.$t("resources.pages.archive_page_title"),
          subtitle: this.$t("resources.pages.archive_page_text"),
          ctaText: "Archive",
        };
        this.archivePageId = item.id;
      },
      async unArchive(item) {
        await this.$admin.dataProvider.customHttpCall("patch", `pages/${item.id}`, {
          status: 1,
        });
        await this.$store.dispatch("api/refresh", "pages");
        this.$admin.toast.success(this.$tc("resources.pages.unarchived_successfully", 1));
      },
      updateSearchFilters: debounce(function (source, value) {
        this.customFiltersValues = {
          ...this.customFiltersValues,
          [source]: value,
        };
      }, 400),
      findStatusName(statusValue) {
        return PAGE_STATUSES.find(status => status.value === statusValue);
      },
      findTypeName(typeValue) {
        return PAGE_TYPES.find(type => type.value === typeValue)?.text;
      },
      async visitLivePage(domain, slug) {
        const id = domain.id;
        let { data } = await this.$admin.dataProvider.getOne("domains", {
          id,
        });

        // TODO: in future check if the object data hasn't changed
        if (slug === "/") {
          window.open(`https://${data.url}/`, "_blank");
        } else {
          window.open(`https://${data.url}/${slug}/`, "_blank");
        }
      },
      editResource(resource, resourceId) {
        this.$router.push({ name: `${resource}_edit`, params: { id: resourceId }, query: { domainId: this.domainId } });
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "@core/preset/preset/functions.scss";
  @import "@core/preset/preset/typography/index.scss";

  ::v-deep {
    .label-cell {
      padding: 12px 0 12px 8px !important;
    }
  }

  .page-table {
    ::v-deep {
      .v-data-table {
        td,
        th {
          padding: 0px 4px;
        }

        th .v-input--checkbox {
          padding: 0 4px;
        }

        .v-data-table__checkbox {
          padding: 0 4px;
        }

        .data-list--action-btn {
          min-width: unset !important;
        }

        .item-actions {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 8px;
        }

        .v-btn--icon {
          width: unset !important;
        }
      }
    }
  }

  .labels-wrapper {
    @include text-caption();
    color: get-color("body");
    display: flex;
    align-items: center;
    gap: 2px;
  }
</style>
